import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SessionType } from '@shared';
import { selectSession } from '@client/model';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import {
  clearIdleSignOutTimeout,
  getIdleSessionEndedPath,
  readIdleSignOutReason,
  storeIdleSignInEmail,
} from '@client/features/nyl-idle-logout/constants';
import { IdleSessionEndedPage } from '@client/features/nyl-idle-logout/IdleSessionEndedPage';

function useCompleteSignOut({
  clerkEmail,
  clerkSignOut,
  idleSessionEndedPath,
  isClient,
  isClerkLoading,
}: {
  clerkEmail: string | null;
  clerkSignOut: (options?: { redirectUrl?: string }) => Promise<void>;
  idleSessionEndedPath: string | null;
  isClient: boolean;
  isClerkLoading: boolean;
}) {
  const hasStartedSignOut = useRef(false);

  useEffect(() => {
    if (isClerkLoading || hasStartedSignOut.current) return;
    hasStartedSignOut.current = true;

    if (idleSessionEndedPath) {
      if (clerkEmail) storeIdleSignInEmail(clerkEmail);
    } else {
      clearIdleSignOutTimeout();
    }
    const redirectUrl = idleSessionEndedPath
      ? idleSessionEndedPath
      : isClient
        ? '/'
        : `/?email=${encodeURIComponent(clerkEmail ?? '')}`;
    clerkSignOut({ redirectUrl });
  }, [clerkEmail, clerkSignOut, idleSessionEndedPath, isClient, isClerkLoading]);
}

export function AuthSignout() {
  const session = useSelector(selectSession);
  const isClient = session.sessionType === SessionType.client;
  const { clerkSignOut, clerkEmail, clerkUserId, isClerkLoading } = useClerkClientAuth();
  const storedIdleSignOutReason = readIdleSignOutReason();
  const matchingIdleTimeoutMinutes =
    !isClerkLoading &&
    storedIdleSignOutReason !== null &&
    (clerkUserId === null || storedIdleSignOutReason.activityScopeId === clerkUserId)
      ? storedIdleSignOutReason.timeoutMinutes
      : null;
  const confirmedIdleTimeoutMinutes = useRef<number | null>(null);
  if (confirmedIdleTimeoutMinutes.current === null && matchingIdleTimeoutMinutes !== null) {
    confirmedIdleTimeoutMinutes.current = matchingIdleTimeoutMinutes;
  }
  const idleTimeoutMinutes = confirmedIdleTimeoutMinutes.current;
  const idleSessionEndedPath =
    idleTimeoutMinutes === null ? null : getIdleSessionEndedPath(idleTimeoutMinutes);

  useCompleteSignOut({
    clerkEmail,
    clerkSignOut,
    idleSessionEndedPath,
    isClient,
    isClerkLoading,
  });

  return idleTimeoutMinutes === null ? null : (
    <IdleSessionEndedPage timeoutMinutes={idleTimeoutMinutes} />
  );
}
